'use client';

import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import type { ProposedActionPayloadT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { useCareStateDomain } from '@/entities/care-state-domain';
import { analytics, ANALYTICS_EVENTS } from '@/shared/lib';

import {
  useProposedActionExecutor,
  useProposedActionMutations,
  type EnrichedProposedAction,
} from '../model';
import { ProposedActionChecklistRow } from './ProposedActionChecklistRow';

const AUTO_EXECUTE_ON_APPROVE = new Set([
  'draft_prescription',
  'draft_existing_lab',
  'draft_lab_order_set',
  'draft_native_lab_order_set',
  'send_follow_up',
  'diagnose',
]);

const BACKGROUND_EXECUTE_ON_APPROVE = new Set(['draft_lab_order_set', 'send_follow_up']);
const FAILED_EXECUTION_STREAM_TIMEOUT_MS = 5_000;

type ActiveMutation =
  | {
      actionId: string;
      kind: 'approve';
      phase: 'requesting' | 'waiting-for-stream';
      releaseWhen: 'decision' | 'execution';
      streamTimeoutAt?: number;
    }
  | {
      actionId: string;
      kind: 'reject';
    };

export interface ProposedActionListProps {
  items: ReadonlyArray<EnrichedProposedAction>;
  emptyState?: ReactNode;
}

export function ProposedActionList({ items, emptyState }: ProposedActionListProps) {
  const { patientUserId } = useCareStateDomain();
  const mutations = useProposedActionMutations();
  const { execute, revise, hasClientExecution } = useProposedActionExecutor();
  const [activeMutation, setActiveMutation] = useState<ActiveMutation | null>(null);

  const pendingActions = useMemo(
    () => items.filter(({ action }) => action.decision === 'Pending'),
    [items]
  );

  const approvedActions = useMemo(
    () => items.filter(({ action }) => action.decision !== 'Pending'),
    [items]
  );

  useEffect(() => {
    if (activeMutation?.kind !== 'approve' || activeMutation.phase !== 'waiting-for-stream') {
      return;
    }

    const currentItem = items.find(({ action }) => action.external_id === activeMutation.actionId);
    const streamCaughtUp =
      currentItem !== undefined &&
      (activeMutation.releaseWhen === 'decision'
        ? currentItem.action.decision !== 'Pending'
        : currentItem.visibility !== 'pending' && currentItem.visibility !== 'executable');

    if (streamCaughtUp) {
      setActiveMutation((current) =>
        current?.kind === 'approve' && current.actionId === activeMutation.actionId ? null : current
      );
      return;
    }

    if (activeMutation.streamTimeoutAt === undefined) return;
    const timeout = window.setTimeout(
      () =>
        setActiveMutation((current) =>
          current?.kind === 'approve' && current.actionId === activeMutation.actionId
            ? null
            : current
        ),
      Math.max(0, activeMutation.streamTimeoutAt - Date.now())
    );
    return () => window.clearTimeout(timeout);
  }, [activeMutation, items]);

  const handleApprove = useCallback(
    async (item: EnrichedProposedAction) => {
      const autoExecuted = AUTO_EXECUTE_ON_APPROVE.has(item.action.payload.tool_name);
      const releaseWhen = BACKGROUND_EXECUTE_ON_APPROVE.has(item.action.payload.tool_name)
        ? 'execution'
        : 'decision';
      setActiveMutation({
        actionId: item.action.external_id,
        kind: 'approve',
        phase: 'requesting',
        releaseWhen,
      });
      try {
        await mutations.approve(item.action.external_id);
        void analytics.track({
          name: ANALYTICS_EVENTS.copilot.proposedActionApproved,
          context: { actor: 'clinician', patientUserId },
          properties: {
            externalId: item.action.external_id,
            label: item.action.title,
            toolName: item.action.payload.tool_name,
            autoExecuted,
          },
        });
        let streamTimeoutAt: number | undefined;
        if (autoExecuted) {
          const executionStarted = await execute(item.action);
          if (!executionStarted) {
            streamTimeoutAt = Date.now() + FAILED_EXECUTION_STREAM_TIMEOUT_MS;
          }
        }
        setActiveMutation((current) =>
          current?.kind === 'approve' && current.actionId === item.action.external_id
            ? { ...current, phase: 'waiting-for-stream', streamTimeoutAt }
            : current
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to approve action';
        toast.error('Cannot approve action', { description: message });
        setActiveMutation(null);
      }
    },
    [mutations, execute, patientUserId]
  );

  const handleReject = useCallback(
    async (item: EnrichedProposedAction, reason: string) => {
      setActiveMutation({ actionId: item.action.external_id, kind: 'reject' });
      try {
        await mutations.reject(item.action.external_id, reason);
        void analytics.track({
          name: ANALYTICS_EVENTS.copilot.proposedActionRejected,
          context: { actor: 'clinician', patientUserId },
          properties: {
            externalId: item.action.external_id,
            label: item.action.title,
            toolName: item.action.payload.tool_name,
            hasReason: reason.trim().length > 0,
          },
        });
      } finally {
        setActiveMutation(null);
      }
    },
    [mutations, patientUserId]
  );

  const handleExecute = useCallback(
    (item: EnrichedProposedAction) => {
      void analytics.track({
        name: ANALYTICS_EVENTS.copilot.proposedActionExecuted,
        context: { actor: 'clinician', patientUserId },
        properties: {
          externalId: item.action.external_id,
          label: item.action.title,
          toolName: item.action.payload.tool_name,
        },
      });
      void execute(item.action);
    },
    [execute, patientUserId]
  );

  const handleRevise = useCallback(
    (item: EnrichedProposedAction, reason: string) => {
      try {
        revise(item.action, reason);
        void analytics.track({
          name: ANALYTICS_EVENTS.copilot.proposedActionRevisionStarted,
          context: { actor: 'clinician', patientUserId },
          properties: {
            externalId: item.action.external_id,
            label: item.action.title,
            toolName: item.action.payload.tool_name,
            hasReason: reason.trim().length > 0,
          },
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to start revision';
        toast.error('Cannot revise action', { description: message });
      }
    },
    [patientUserId, revise]
  );

  const itemsByExternalId = useMemo(
    () => new Map(items.map((item) => [item.action.external_id, item])),
    [items]
  );

  const handleUpdatePayload = useCallback(
    async (externalId: string, payload: ProposedActionPayloadT): Promise<void> => {
      try {
        await mutations.updatePayload(externalId, payload);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save edits';
        toast.error('Cannot save edits', { description: message });
        throw err;
      }
    },
    [mutations]
  );

  const handleSwitchAlternative = useCallback(
    (externalId: string, alternativePayloadId: string) => {
      const item = itemsByExternalId.get(externalId);
      if (item) {
        void analytics.track({
          name: ANALYTICS_EVENTS.copilot.proposedActionAlternativeSwitched,
          context: { actor: 'clinician', patientUserId },
          properties: {
            externalId,
            label: item.action.title,
            toolName: item.action.payload.tool_name,
            alternativePayloadId,
          },
        });
      }
      void mutations.switchAlternative(externalId, alternativePayloadId);
    },
    [mutations, itemsByExternalId, patientUserId]
  );

  const orderedItems = useMemo(
    () => [...pendingActions, ...approvedActions],
    [pendingActions, approvedActions]
  );

  const renderActionRow = useCallback(
    (item: EnrichedProposedAction) => {
      const isPending = item.action.decision === 'Pending';
      const clientExecutable = hasClientExecution(item.action.payload.tool_name);
      const isApproving =
        activeMutation?.actionId === item.action.external_id && activeMutation.kind === 'approve';
      const isRejecting =
        activeMutation?.actionId === item.action.external_id && activeMutation.kind === 'reject';

      return (
        <ProposedActionChecklistRow
          key={item.action.external_id}
          item={item}
          onApprove={
            isPending
              ? (nextItem) => {
                  void handleApprove(nextItem);
                }
              : undefined
          }
          onReject={(nextItem, reason) => {
            void handleReject(nextItem, reason);
          }}
          onRevise={
            clientExecutable && item.action.payload.tool_name !== 'send_follow_up'
              ? (nextItem, reason) => {
                  handleRevise(nextItem, reason);
                }
              : undefined
          }
          onExecute={!isPending && clientExecutable ? handleExecute : undefined}
          onSwitchAlternative={handleSwitchAlternative}
          onUpdatePayload={isPending ? handleUpdatePayload : undefined}
          isApproving={isApproving}
          isRejecting={isRejecting}
        />
      );
    },
    [
      activeMutation,
      handleApprove,
      handleExecute,
      handleReject,
      handleRevise,
      handleSwitchAlternative,
      handleUpdatePayload,
      hasClientExecution,
    ]
  );

  if (items.length === 0) {
    return emptyState ?? null;
  }

  return (
    <section className="pb-1">
      <div className="max-h-[320px] overflow-y-auto">
        {orderedItems.map((item) => renderActionRow(item))}
      </div>
    </section>
  );
}
